import React, { useEffect, useState } from 'react';
import axiosInstance from '../variables/api/axiosInstance';
import { PROFILS_LABELS } from '../variables/constants';

/**
 * L'image de signature d'un document, posée dans l'espace que le document réserve déjà.
 *
 * POURQUOI ELLE NE S'AJOUTE PAS, ELLE SE LOGE. Les documents réservent un espace vide
 * sous le libellé de fonction - 16 mm sur les attestations - parce qu'on y signait à
 * la main. L'image est bornée à cette hauteur : elle prend la place du vide, sans en
 * ajouter. C'est ce qui garantit qu'un document qui tenait sur une page A4 y tienne
 * encore. Ajouter un bloc sous le libellé aurait poussé le pied de page, et souvent
 * fait déborder sur une seconde page ne portant que lui.
 *
 * POURQUOI RIEN N'EST AFFICHÉ EN CAS D'ÉCHEC. Trois raisons empêchent le serveur de
 * rendre une image : le document n'est pas paramétré comme signé, la fonction n'a pas
 * de titulaire, ou ce titulaire n'a pas de signature VALIDE. Toutes rendent le même
 * refus, volontairement - les distinguer laisserait deviner qui signe quoi. Ici, les
 * trois donnent le même résultat : l'espace reste vide, et le document sort comme
 * avant. Jamais d'erreur à l'écran, jamais d'impression bloquée.
 *
 * POURQUOI L'IMAGE S'EFFACE TOUTE SEULE. Les signatures sont temporaires. Une fois
 * l'image chargée, elle vit dans le navigateur : un écran d'impression laissé ouvert
 * imprimerait, une heure plus tard, une signature que le serveur refuse déjà de
 * servir. Le compte à rebours renvoyé par le serveur sert donc à la retirer de
 * l'écran à l'instant où elle expire. Le délai vient du serveur et non d'une horloge
 * locale : celle d'un poste se règle à la main.
 *
 * POURQUOI UNE SIGNATURE PEUT ARRIVER TOUTE PRÊTE. Quatre documents s'impriment par
 * `renderToStaticMarkup`, qui sérialise le rendu en chaîne de caractères : aucun effet
 * React ne s'y exécute, et ce composant n'y chargeait donc jamais rien. Ces
 * documents-là reçoivent la signature en `signature`, chargée avant le rendu par
 * `chargerSignatureImprimable`. Le composant ne fait alors qu'afficher - c'est le seul
 * mode qui fonctionne hors du DOM vivant.
 *
 * @param codeDocument code du catalogue serveur (FICHE_DECLARATION…)
 * @param signature signature déjà chargée `{ image, nom, fonction }`, pour les rendus
 *        statiques. Quand elle est fournie, aucun appel n'est émis.
 * @param sinistreTrackingId dossier concerné, pour la trace ; facultatif
 * @param hauteurMm hauteur de l'espace réservé par le document
 * @param actif  false quand le responsable décoche « apposer ma signature »
 * @param avecIdentite affiche le nom et la fonction sous l'image. FAUX par défaut,
 *        et c'est délibéré : ces deux lignes ajoutent près de 8 mm sous la
 *        signature. Sur un document dont la marge A4 se compte en millimètres -
 *        les notes à l'expert et à la victime n'en ont que quatre -, les ajouter
 *        d'office ferait déborder sur une seconde page. Chaque document l'active
 *        après qu'on a vérifié qu'il a la place.
 */
export default function BlocSignature({
  codeDocument,
  sinistreTrackingId,
  hauteurMm = 16,
  actif = true,
  alignement = 'right',
  avecIdentite = false,
  signature = null,
}) {
  const [image, setImage] = useState(null);
  /* Le nom et la fonction voyagent à part de l'image : l'un est du texte, l'autre
     des octets. Les réunir aurait obligé à encoder l'image dans du JSON, un tiers
     de poids en plus, pour économiser un appel qui part en même temps. */
  const [signataire, setSignataire] = useState(null);

  useEffect(() => {
    // Signature fournie : rien à charger, et surtout rien à charger DEUX fois.
    if (signature || !actif || !codeDocument) { setImage(null); return undefined; }

    let ignore = false;
    let url = null;
    let minuterie = null;

    axiosInstance
      .get(`/v1/signatures/document/${codeDocument}`, {
        params: sinistreTrackingId ? { sinistre: sinistreTrackingId } : undefined,
        responseType: 'blob',
        // Le refus est une réponse normale ici, pas un incident : l'immense majorité
        // des documents n'est pas signée.
        skipErrorToast: true,
      })
      .then((r) => {
        if (ignore) return;
        url = URL.createObjectURL(r.data);
        setImage(url);
      })
      .catch(() => { if (!ignore) setImage(null); });

    axiosInstance
      .get(`/v1/signatures/document/${codeDocument}/signataire`, { skipErrorToast: true })
      .then((r) => { if (!ignore) setSignataire(r.data?.data || null); })
      // Sans identité, la signature s'affiche seule : mieux vaut une signature sans
      // nom qu'un document qui refuse de sortir.
      .catch(() => { if (!ignore) setSignataire(null); });

    axiosInstance
      .get(`/v1/signatures/document/${codeDocument}/etat-impression`, { skipErrorToast: true })
      .then((r) => {
        const restant = r.data?.data?.minutesRestantes;
        if (ignore || !restant || restant <= 0) return;
        /* Une minute de battement : le serveur arrondit au supérieur, et retirer
           l'image une seconde trop tôt priverait de sa signature un tirage encore
           dans les temps. Trop tard ne coûte rien - le serveur, lui, refuse déjà. */
        minuterie = setTimeout(() => setImage(null), (restant + 1) * 60 * 1000);
      })
      .catch(() => { });

    return () => {
      ignore = true;
      if (url) URL.revokeObjectURL(url);
      if (minuterie) clearTimeout(minuterie);
    };
  }, [codeDocument, sinistreTrackingId, actif, signature]);

  /* La signature fournie l'emporte : c'est le mode des rendus statiques, où l'état
     ci-dessus reste vide par construction. */
  const source = signature ? signature.image : image;
  const identite = signature || signataire;
  if (!actif || !source) return null;

  const fonction = identite?.fonction
    ? (PROFILS_LABELS[identite.fonction] || identite.fonction)
    : null;

  return (
    <div style={{ textAlign: alignement }}>
      <img
        src={source}
        alt=""
        style={{
          display: 'block',
          maxHeight: `${hauteurMm}mm`,
          maxWidth: '55mm',
          marginLeft: alignement === 'right' ? 'auto' : 0,
          marginRight: alignement === 'right' ? 0 : 'auto',
          objectFit: 'contain',
        }}
      />
      {/* Un trait sans auteur ne prouve rien au lecteur : le document doit dire de
          qui il tient. Le nom en gras, la fonction en dessous - l'ordre dont on lit
          une signature manuscrite. */}
      {avecIdentite && identite?.nom && (
        <div style={{ marginTop: '1mm', lineHeight: 1.25 }}>
          <div style={{ fontWeight: 700, fontSize: '9.5pt' }}>{identite.nom}</div>
          {fonction && <div style={{ fontSize: '8.5pt' }}>{fonction}</div>}
        </div>
      )}
    </div>
  );
}
